import type { EventRule } from '#src/graphql/generated';

import { useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  Heading,
  Text,
  VStack,
  Button,
  HStack,
  Input,
  Switch,
  Tabs,
} from '@chakra-ui/react';
import { useState } from 'react';

import { JsonEditor } from '#src/components/json-editor';
import { toaster } from '#src/components/ui/toaster';
import { PROCESS_EVENT } from '#src/graphql/mutations/process-event';
import { UPSERT_EVENT_RULE } from '#src/graphql/mutations/upsert-event';
import { GET_EVENT_RULES } from '#src/graphql/queries/events';

export function EventsPage() {
  const [selectedEvent, setSelectedEvent] = useState<EventRule | null>(null);
  const [editName, setEditName] = useState('');
  const [editEnabled, setEditEnabled] = useState(true);
  const [editContext, setEditContext] = useState('');
  const [editCondition, setEditCondition] = useState('');
  const [editPreparation, setEditPreparation] = useState('');
  const [editApplication, setEditApplication] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [newEventName, setNewEventName] = useState('');

  // Process Event form state
  const [processEventType, setProcessEventType] = useState('');
  const [processEventOperation, setProcessEventOperation] = useState('');
  const [processEventSourceId, setProcessEventSourceId] = useState('');
  const [processEventDryRun, setProcessEventDryRun] = useState(false);
  const [processEventResult, setProcessEventResult] = useState<any>(null);

  const { data, loading, error, refetch } = useQuery(GET_EVENT_RULES);
  const [upsertEventRule, { loading: saving }] = useMutation(UPSERT_EVENT_RULE);
  const [processEvent, { loading: processing }] = useMutation(PROCESS_EVENT);

  const events = data?.eventRules?.items || [];

  const handleEventSelect = (event: EventRule) => {
    setSelectedEvent(event);
    setEditName(event.name);
    setEditEnabled(event.enabled);
    setEditContext(event.context);
    setEditCondition(event.condition);
    setEditPreparation(event.preparation);
    setEditApplication(event.application);
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!selectedEvent) return;

    try {
      await upsertEventRule({
        variables: {
          input: {
            id: selectedEvent.id,
            name: editName,
            enabled: editEnabled,
            context: editContext,
            condition: editCondition,
            preparation: editPreparation,
            application: editApplication,
          },
        },
      });

      toaster.create({
        title: 'Success',
        description: 'Event rule saved successfully',
      });

      // Refresh the events list
      await refetch();

      // Update the selected event with new data
      const updatedEvent = events.find(
        (event: EventRule) => event.id === selectedEvent.id,
      );
      if (updatedEvent) {
        setSelectedEvent(updatedEvent);
      }

      setIsEditing(false);
    } catch (error) {
      toaster.create({
        title: 'Error',
        description: `Error saving event rule: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    }
  };

  const handleCancel = () => {
    if (selectedEvent) {
      setEditName(selectedEvent.name);
      setEditEnabled(selectedEvent.enabled);
      setEditContext(selectedEvent.context);
      setEditCondition(selectedEvent.condition);
      setEditPreparation(selectedEvent.preparation);
      setEditApplication(selectedEvent.application);
    }
    setIsEditing(false);
  };

  const handleCreateEvent = () => {
    if (!newEventName.trim()) {
      toaster.create({
        title: 'Validation Error',
        description: 'Please enter an event rule name',
      });
      return;
    }

    // Create a new event object for editing
    const newEvent: EventRule = {
      id: '', // Will be generated by the backend
      userId: '',
      name: newEventName.trim(),
      enabled: true,
      context: '',
      condition: '',
      preparation: '',
      application: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    setSelectedEvent(newEvent);
    setEditName(newEvent.name);
    setEditEnabled(newEvent.enabled);
    setEditContext(newEvent.context);
    setEditCondition(newEvent.condition);
    setEditPreparation(newEvent.preparation);
    setEditApplication(newEvent.application);
    setIsEditing(true);
    setNewEventName('');
  };

  const handleProcessEvent = async () => {
    if (
      !processEventType.trim() ||
      !processEventOperation.trim() ||
      !processEventSourceId.trim()
    ) {
      toaster.create({
        title: 'Validation Error',
        description: 'Please fill in all fields (Type, Operation, Source ID)',
      });
      return;
    }

    try {
      const { data } = await processEvent({
        variables: {
          input: {
            type: processEventType.trim(),
            operation: processEventOperation.trim(),
            sourceId: processEventSourceId.trim(),
            dryRun: processEventDryRun,
          },
        },
      });

      setProcessEventResult(data?.processEvent);

      toaster.create({
        title: data?.processEvent?.success ? 'Success' : 'Error',
        description: data?.processEvent?.message || 'Event processed',
        type: data?.processEvent?.success ? 'success' : 'error',
      });
    } catch (error) {
      toaster.create({
        title: 'Error',
        description: `Error processing event: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error',
      });
    }
  };

  if (loading) {
    return (
      <Box>
        <Heading size="lg" color="gray.700" mb={6}>
          Event Rules
        </Heading>
        <Text>Loading event rules...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Heading size="lg" color="gray.700" mb={6}>
          Event Rules
        </Heading>
        <Text color="red.500">Error loading event rules: {error.message}</Text>
        <Button onClick={() => refetch()} mt={4}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Heading size="lg" color="gray.700" mb={6}>
        Event Rules
      </Heading>

      {/* Process Event Section */}
      <Box mb={8}>
        <Box bg="white" shadow="lg" borderRadius="lg" p={6}>
          <VStack gap={4} align="stretch">
            <Box>
              <Heading size="md" color="gray.700" mb={2}>
                Process Event
              </Heading>
              <Text fontSize="sm" color="gray.600">
                Test your event rules by processing a sample event. Use Dry Run
                to evaluate conditions without executing preparation/application
                logic.
              </Text>
            </Box>

            <HStack gap={4} align="start">
              <VStack gap={3} align="stretch" flex={1}>
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Event Type
                  </Text>
                  <Input
                    value={processEventType}
                    onChange={(e) => setProcessEventType(e.target.value)}
                    placeholder="e.g., message, document, user_action"
                    size="sm"
                  />
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Operation
                  </Text>
                  <Input
                    value={processEventOperation}
                    onChange={(e) => setProcessEventOperation(e.target.value)}
                    placeholder="e.g., create, update, delete"
                    size="sm"
                  />
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Source ID
                  </Text>
                  <Input
                    value={processEventSourceId}
                    onChange={(e) => setProcessEventSourceId(e.target.value)}
                    placeholder="e.g., msg_123, doc_456"
                    size="sm"
                  />
                </Box>
                <HStack justify="space-between" align="center">
                  <Text fontSize="sm" fontWeight="medium">
                    Dry Run
                  </Text>
                  <Switch.Root
                    checked={processEventDryRun}
                    onCheckedChange={(e) => setProcessEventDryRun(e.checked)}
                  />
                </HStack>
                <Button
                  onClick={handleProcessEvent}
                  colorScheme="blue"
                  size="sm"
                  loading={processing}
                  loadingText="Processing..."
                  disabled={
                    !processEventType.trim() ||
                    !processEventOperation.trim() ||
                    !processEventSourceId.trim()
                  }
                >
                  Process Event
                </Button>
              </VStack>

              {/* Results Display */}
              {processEventResult && (
                <Box flex={1}>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Result
                  </Text>
                  <Box
                    bg={processEventResult.success ? 'green.50' : 'red.50'}
                    border="1px solid"
                    borderColor={
                      processEventResult.success ? 'green.200' : 'red.200'
                    }
                    borderRadius="md"
                    p={3}
                  >
                    <VStack gap={2} align="stretch">
                      <HStack justify="space-between" align="center">
                        <Text fontSize="sm" fontWeight="medium">
                          Status:{' '}
                          {processEventResult.success ? 'Success' : 'Error'}
                        </Text>
                        <Box
                          w={2}
                          h={2}
                          borderRadius="full"
                          bg={
                            processEventResult.success ? 'green.500' : 'red.500'
                          }
                        />
                      </HStack>
                      <Text fontSize="sm" color="gray.700">
                        {processEventResult.message}
                      </Text>
                      {processEventResult.error && (
                        <Text fontSize="sm" color="red.600">
                          Error: {processEventResult.error}
                        </Text>
                      )}
                    </VStack>
                  </Box>
                </Box>
              )}
            </HStack>
          </VStack>
        </Box>
      </Box>

      <HStack gap={4} align="start" height="calc(100vh - 400px)">
        {/* Events List - File Explorer Style */}
        <Box width="320px" minWidth="320px">
          <Box bg="white" shadow="lg" borderRadius="lg" p={4} height="100%">
            <VStack gap={3} align="stretch" height="100%">
              {/* Header with Create Button */}
              <HStack justify="space-between" align="center">
                <Text fontWeight="semibold" fontSize="sm" color="gray.700">
                  Event Rules
                </Text>
                <HStack gap={2}>
                  <Button onClick={() => refetch()} size="xs" variant="ghost">
                    ↻
                  </Button>
                  <Button
                    onClick={handleCreateEvent}
                    size="xs"
                    colorScheme="blue"
                  >
                    + New
                  </Button>
                </HStack>
              </HStack>

              {/* Create Event Input */}
              <HStack gap={2}>
                <Input
                  placeholder="Event rule name..."
                  value={newEventName}
                  onChange={(e) => setNewEventName(e.target.value)}
                  size="sm"
                  fontSize="sm"
                />
                <Button
                  onClick={handleCreateEvent}
                  size="sm"
                  colorScheme="blue"
                  disabled={!newEventName.trim()}
                >
                  Create
                </Button>
              </HStack>

              {/* Events List */}
              <Box flex={1} overflowY="auto">
                {events.length === 0 ? (
                  <Text
                    color="gray.500"
                    textAlign="center"
                    py={8}
                    fontSize="sm"
                  >
                    No event rules yet
                  </Text>
                ) : (
                  <VStack gap={1} align="stretch">
                    {events.map((event: EventRule) => (
                      <Box
                        key={event.id}
                        p={3}
                        borderRadius="md"
                        cursor="pointer"
                        bg={
                          selectedEvent?.id === event.id
                            ? 'blue.50'
                            : 'transparent'
                        }
                        borderLeft={
                          selectedEvent?.id === event.id
                            ? '3px solid'
                            : '3px solid transparent'
                        }
                        borderLeftColor={
                          selectedEvent?.id === event.id
                            ? 'blue.500'
                            : 'transparent'
                        }
                        _hover={{ bg: 'gray.50' }}
                        onClick={() => handleEventSelect(event)}
                      >
                        <VStack gap={1} align="stretch">
                          <HStack justify="space-between" align="start">
                            <Text
                              fontWeight="medium"
                              fontSize="sm"
                              lineClamp={1}
                              color="gray.800"
                            >
                              {event.name}
                            </Text>
                            <HStack gap={1}>
                              <Box
                                w={2}
                                h={2}
                                borderRadius="full"
                                bg={event.enabled ? 'green.500' : 'gray.400'}
                              />
                              <Text fontSize="xs" color="gray.500">
                                {new Date(event.createdAt).toLocaleDateString()}
                              </Text>
                            </HStack>
                          </HStack>
                          <Text fontSize="xs" color="gray.500" lineClamp={2}>
                            {event.context}
                          </Text>
                        </VStack>
                      </Box>
                    ))}
                  </VStack>
                )}
              </Box>
            </VStack>
          </Box>
        </Box>

        {/* Event Editor - Takes remaining space */}
        <Box flex={1}>
          {selectedEvent ? (
            <Box bg="white" shadow="lg" borderRadius="lg" p={6} height="100%">
              <VStack gap={4} align="stretch" height="100%">
                <HStack justify="space-between" align="center">
                  <Heading size="lg" color="gray.700">
                    {selectedEvent.name || 'New Event Rule'}
                  </Heading>
                  {isEditing && (
                    <HStack>
                      <Button size="sm" onClick={handleCancel}>
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        onClick={handleSave}
                        loading={saving}
                        loadingText="Saving..."
                      >
                        Save
                      </Button>
                    </HStack>
                  )}
                </HStack>

                <Box flex={1} overflowY="auto">
                  <VStack gap={4} align="stretch">
                    {/* Basic Info */}
                    <Box>
                      <Text fontWeight="semibold" mb={2}>
                        Basic Information
                      </Text>
                      <VStack gap={3} align="stretch">
                        <Box>
                          <Text fontSize="sm" fontWeight="medium" mb={2}>
                            Name
                          </Text>
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            placeholder="Enter event rule name..."
                            disabled={!isEditing}
                          />
                        </Box>
                        <HStack justify="space-between" align="center">
                          <Text fontSize="sm" fontWeight="medium">
                            Enabled
                          </Text>
                          <Switch.Root
                            checked={editEnabled}
                            onCheckedChange={(e) => setEditEnabled(e.checked)}
                            disabled={!isEditing}
                          />
                        </HStack>
                      </VStack>
                    </Box>

                    {/* Tabbed Editor for Event Rule Sections */}
                    <Box>
                      <VStack gap={2} align="stretch">
                        <Text fontWeight="semibold">
                          Event Rule Configuration
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          Use the Condition tab to define JSON rules for
                          json-rules-engine. Switch between Text and JSON modes
                          for better editing experience.
                        </Text>
                      </VStack>
                      <Tabs.Root defaultValue="context" variant="enclosed">
                        <Tabs.List>
                          <Tabs.Trigger value="context">Context</Tabs.Trigger>
                          <Tabs.Trigger value="condition">
                            Condition
                          </Tabs.Trigger>
                          <Tabs.Trigger value="preparation">
                            Preparation
                          </Tabs.Trigger>
                          <Tabs.Trigger value="application">
                            Application
                          </Tabs.Trigger>
                        </Tabs.List>

                        <Tabs.Content value="context">
                          <VStack gap={3} align="stretch">
                            <Box>
                              <Text fontSize="sm" fontWeight="medium" mb={2}>
                                Event Context
                              </Text>
                              <Text fontSize="xs" color="gray.500" mb={3}>
                                Define the context for this event rule. This can
                                be plain text or structured data.
                              </Text>
                            </Box>
                            <JsonEditor
                              value={editContext}
                              onChange={setEditContext}
                              placeholder="Enter context for the event rule..."
                              height="400px"
                              disabled={!isEditing}
                            />
                          </VStack>
                        </Tabs.Content>

                        <Tabs.Content value="condition">
                          <VStack gap={3} align="stretch">
                            <Box>
                              <Text fontSize="sm" fontWeight="medium" mb={2}>
                                JSON Rules Engine Condition
                              </Text>
                              <Text fontSize="xs" color="gray.500" mb={3}>
                                Define the condition logic using
                                json-rules-engine format. Example: Check if
                                eventType equals "message"
                              </Text>
                            </Box>
                            <JsonEditor
                              value={editCondition}
                              onChange={setEditCondition}
                              placeholder={`Example JSON rule:
{
  "conditions": {
    "all": [
      {
        "fact": "eventType",
        "operator": "equal",
        "value": "message"
      }
    ]
  },
  "event": {
    "type": "messageEvent",
    "params": {
      "message": "Message event detected"
    }
  }
}`}
                              height="400px"
                              disabled={!isEditing}
                            />
                          </VStack>
                        </Tabs.Content>

                        <Tabs.Content value="preparation">
                          <VStack gap={3} align="stretch">
                            <Box>
                              <Text fontSize="sm" fontWeight="medium" mb={2}>
                                Preparation Logic
                              </Text>
                              <Text fontSize="xs" color="gray.500" mb={3}>
                                Define the preparation logic that runs before
                                the main application logic. This can be plain
                                text, code, or structured data.
                              </Text>
                            </Box>
                            <JsonEditor
                              value={editPreparation}
                              onChange={setEditPreparation}
                              placeholder="Enter preparation logic for the event rule..."
                              height="400px"
                              disabled={!isEditing}
                            />
                          </VStack>
                        </Tabs.Content>

                        <Tabs.Content value="application">
                          <VStack gap={3} align="stretch">
                            <Box>
                              <Text fontSize="sm" fontWeight="medium" mb={2}>
                                Application Logic
                              </Text>
                              <Text fontSize="xs" color="gray.500" mb={3}>
                                Define the main application logic that runs when
                                the event rule conditions are met. This can be
                                plain text, code, or structured data.
                              </Text>
                            </Box>
                            <JsonEditor
                              value={editApplication}
                              onChange={setEditApplication}
                              placeholder="Enter application logic for the event rule..."
                              height="400px"
                              disabled={!isEditing}
                            />
                          </VStack>
                        </Tabs.Content>
                      </Tabs.Root>
                    </Box>
                  </VStack>
                </Box>

                {!isEditing && (
                  <Button
                    colorScheme="blue"
                    onClick={() => setIsEditing(true)}
                    alignSelf="start"
                  >
                    Edit Event Rule
                  </Button>
                )}
              </VStack>
            </Box>
          ) : (
            <Box
              bg="white"
              shadow="lg"
              borderRadius="lg"
              p={12}
              height="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <VStack gap={4}>
                <Text fontSize="lg" color="gray.500" textAlign="center">
                  Select an event rule to view and edit
                </Text>
                <Text fontSize="sm" color="gray.400" textAlign="center">
                  Choose an event rule from the list on the left to get started
                </Text>
              </VStack>
            </Box>
          )}
        </Box>
      </HStack>
    </Box>
  );
}
